import React, { useCallback, useState } from "react";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const CoverImageUploader = ({ 
  value, 
  onChange, 
  language = "it",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === "it" 
        ? "Tipo file non supportato. Usa JPG, PNG, GIF o WebP" 
        : "File type not supported. Use JPG, PNG, GIF or WebP"
      );
      return;
    }

    // Validate file size (10MB for cover images)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === "it" ? "File troppo grande. Max 10MB" : "File too large. Max 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/cover`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      onChange(response.data.url);
      toast.success(language === "it" ? "Foto caricata!" : "Photo uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore nel caricamento" : "Upload error"));
    } finally {
      setUploading(false);
    }
  }, [onChange, language]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleRemove = useCallback(() => {
    onChange("");
  }, [onChange]);

  return (
    <div className={className}>
      {value ? (
        // Preview mode
        <div className="relative rounded-xl overflow-hidden border-2 border-border">
          <img 
            src={value} 
            alt="Cover preview" 
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-between">
            <div className="text-white">
              <p className="text-sm font-medium">
                {language === "it" ? "Foto di copertina caricata" : "Cover photo uploaded"}
              </p>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="p-2 rounded-full bg-white/20 hover:bg-red-500 text-white transition-colors backdrop-blur-sm"
              data-testid="remove-cover-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        // Upload mode
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden
            ${dragOver 
              ? "border-violet-500 bg-violet-50 dark:bg-violet-950/20" 
              : "border-slate-300 dark:border-slate-600 hover:border-violet-400 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            }
          `}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            disabled={uploading}
            data-testid="cover-file-input"
          />
          
          <div className="p-8 text-center">
            {uploading ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {language === "it" ? "Caricamento in corso..." : "Uploading..."}
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-100 to-pink-100 dark:from-violet-900/30 dark:to-pink-900/30 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-violet-500" />
                </div>
                <p className="text-base font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === "it" 
                    ? "Trascina qui la foto o clicca per selezionare" 
                    : "Drag photo here or click to select"
                  }
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  JPG, PNG, GIF, WebP • Max 10MB
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  {language === "it" 
                    ? "Consigliato: 1200x400 px per un risultato ottimale" 
                    : "Recommended: 1200x400 px for best results"
                  }
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverImageUploader;
